import { colorToRGBA } from "src/utils/colorToRgba";
import React, { useRef, useState } from "react";
import { Color, Flex, Icon, Spinner, Text } from "src/elements";
import { useHoverClickable } from "src/utils/hoverClickable";
import styled from "styled-components";
import { styledSafe } from "src/utils/styledComponent";
import { filterValidFiles, isZipFile, mergeFilesWithDedup } from "./utils";

const UploaderElement = styled(Flex)`
  position: relative;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-width: 1px;
  border-radius: 4px;
`;

const HiddenInput = styledSafe("input")<{ isDisabled: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  opacity: 0;
  cursor: pointer;
  pointer-events: ${({ isDisabled }) => isDisabled && "none"};
`;

const FileListWrapper = styled(Flex)`
  width: 100%;
  max-height: 120px;
  overflow-y: auto;
  flex-direction: column;
  gap: 4px;
`;

const FileRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${colorToRGBA(Color.primary, 0.06)};
`;

const RemoveButton = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  opacity: 0.6;
  &:hover {
    opacity: 1;
  }
`;

const CONFIG = {
  "psa-zip": {
    accept: ".psa,.zip",
    extensions: [".psa", ".zip"],
    label: "Click or drag your PSA / ZIP files here to upload",
    typeLabel: "PSA / ZIP Files",
    errorLabel: "Only .psa and .zip files are accepted",
    icon: "filePsa",
  },
  jpg: {
    accept: ".jpg,.jpeg,.png",
    extensions: [".jpg", ".jpeg", ".png"],
    label: "Click or drag your JPG / PNG files here to upload",
    typeLabel: "JPG / PNG Files",
    errorLabel: "Only .jpg, .jpeg, and .png files are accepted",
    icon: "fileCsv",
  },
  "jda-baseline": {
    accept: ".csv,.txt",
    extensions: [".csv", ".txt"],
    label: "Click or drag your JDA Site, JDA Shelf, and Article Master files here",
    typeLabel: "CSV / TXT Files",
    errorLabel: "Only .csv and .txt files are accepted",
    icon: "fileCsv",
  },
} as const;

interface Props {
  isLoading: boolean;
  onChange: (files: File[]) => void;
  onSuccess?: React.ReactNode;
  fileType?: keyof typeof CONFIG;
  maxFiles?: number;
}

export const MultiFileUploader = ({
  isLoading,
  onChange,
  onSuccess,
  fileType = "psa-zip",
  maxFiles,
}: Props) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [invalidFiles, setInvalidFiles] = useState<string[]>([]);

  const refFileInput = useRef<HTMLInputElement>(null);
  const { isHovered } = useHoverClickable(refFileInput);

  const hasFiles = files.length > 0;
  const hasError = invalidFiles.length > 0;
  const isAtLimit = maxFiles !== undefined && files.length >= maxFiles;

  const status =
    (onSuccess && "SUCCESS") || (hasError && "ERROR") || (hasFiles && "SELECTED") || "EMPTY";

  const backgroundColor =
    ((status === "SUCCESS" || (status === "SELECTED" && (isDragging || isHovered))) &&
      Color.greenSmoke) ||
    (status === "SELECTED" && Color.greenSmokeDisabled) ||
    (status === "ERROR" && (isDragging || isHovered) && colorToRGBA(Color.red, 0.4)) ||
    (status === "ERROR" && colorToRGBA(Color.red, 0.3)) ||
    ((isDragging || isHovered) && Color.opacitySkyHover) ||
    Color.opacitySky;

  const borderColor =
    ((status === "SUCCESS" || status === "SELECTED") && Color.greenSmokeHover) ||
    (status === "ERROR" && Color.redDisabled) ||
    Color.opacitySkyHover;

  const iconColor =
    ((status === "SUCCESS" || status === "SELECTED") && Color.primary) ||
    (status === "ERROR" && Color.red) ||
    Color.opacitySkyActive;

  const textColor =
    ((status === "SUCCESS" || status === "SELECTED") && Color.primaryActive) ||
    (status === "ERROR" && Color.redActive) ||
    Color.textSecondary;

  const startDragging = () => setIsDragging(true);
  const stopDragging = () => setIsDragging(false);

  const config = CONFIG[fileType];

  const updateFiles = (newFiles: File[]) => {
    const { valid, invalid } = filterValidFiles(newFiles, [...config.extensions]);
    setInvalidFiles(invalid);

    if (valid.length > 0) {
      const merged = mergeFilesWithDedup(files, valid);
      const capped = maxFiles !== undefined ? merged.slice(0, maxFiles) : merged;
      setFiles(capped);
      onChange(capped);
    }
  };

  const removeFile = (index: number) => {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
    setInvalidFiles([]);
    onChange(updated);
  };

  const onFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files;
    if (!selected || selected.length === 0) return;
    updateFiles(Array.from(selected));
    if (refFileInput.current) {
      refFileInput.current.value = "";
    }
  };

  return (
    <Flex column gap="8px" width="100%">
      <UploaderElement
        background={backgroundColor}
        borderColor={borderColor}
        borderStyle={isDragging || isHovered ? "dashed" : "solid"}
      >
        <Icon name={config.icon} color={iconColor} />

        <Text variant="small1" color={textColor} textAlign="center">
          {hasFiles ? `${files.length} file(s) selected` : config.label}
        </Text>

        {isLoading && <Spinner size="medium" color={Color.primary} />}

        {status === "SUCCESS" && <Icon name="checkmark" size={32} />}

        {!isLoading && !onSuccess && (
          <>
            <Text variant="small1" color={iconColor} textAlign="center">
              {hasError ? config.errorLabel : config.typeLabel}
            </Text>

            {maxFiles !== undefined && !hasError && (
              <Text variant="small2" color={Color.textSecondary} textAlign="center">
                {`Up to ${maxFiles} files per upload`}
              </Text>
            )}
          </>
        )}

        {onSuccess && !isLoading && (
          <Text variant="small1" color={Color.primary} textAlign="center">
            {onSuccess}
          </Text>
        )}

        <HiddenInput
          ref={refFileInput}
          type="file"
          multiple
          onChange={onFilesSelected}
          onDragOver={startDragging}
          onDragLeave={stopDragging}
          onDrop={stopDragging}
          title=" "
          accept={config.accept}
          isDisabled={isLoading || Boolean(onSuccess) || isAtLimit}
        />
      </UploaderElement>

      {hasFiles && !onSuccess && (
        <FileListWrapper>
          {files.map((file, index) => (
            <FileRow key={`${file.name}-${file.size}`}>
              <Flex align="center" gap="6px">
                <Icon
                  name={isZipFile(file.name) ? "fileCsv" : config.icon}
                  size={14}
                  color={Color.primary}
                />
                <Text variant="small2" color={Color.primaryActive}>
                  {file.name}
                </Text>
              </Flex>
              {!isLoading && (
                <RemoveButton onClick={() => removeFile(index)} aria-label={`Remove ${file.name}`}>
                  <Icon name="clearCircle" size={14} color={Color.red} />
                </RemoveButton>
              )}
            </FileRow>
          ))}
        </FileListWrapper>
      )}

      {hasError && (
        <Text variant="small2" color={Color.red}>
          Skipped invalid files: {invalidFiles.join(", ")}
        </Text>
      )}

      {isAtLimit && (
        <Text variant="small2" color={Color.textSecondary}>
          Maximum {maxFiles} files reached. Remove a file to add more.
        </Text>
      )}
    </Flex>
  );
};
